import { changePassword, deleteAccount } from "../redux/slices/authSlice";
import apiClient from "./config";
import { AUTH_ENDPOINTS } from "./endpoints";
import { getRefreshToken } from "../utils/auth";

/**
 * Authentication & Registration Service
 * Centralized API calls for authentication, registration, and user management
 */
const authService = {
  // ========== Authentication ==========

  // Login user
  login: (credentials) => {
    return apiClient.post(AUTH_ENDPOINTS.LOGIN, credentials);
  },

  // Register user (legacy)
  register: (userData) => {
    return apiClient.post(AUTH_ENDPOINTS.REGISTER, userData);
  },

  // Logout user — send the refresh token so the backend revokes only THIS
  // device's token family (other devices stay logged in).
  logout: () => {
    return apiClient.post(AUTH_ENDPOINTS.LOGOUT, {
      refreshToken: getRefreshToken(),
    });
  },

  // ========== Registration Flow ==========

  // Register Step One - Initial signup with name, email, phone, password
  registerStepOne: (userData) => {
    return apiClient.post(AUTH_ENDPOINTS.REGISTER_STEP_ONE, userData);
  },

  // Verify OTP for Login/Signup flow
  verifyOtpLoginSignup: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.VERIFY_OTP_LOGIN_SIGNUP, data);
  },

  // Complete Profile - Submit all 8 steps data (multipart/form-data)
  completeProfile: (formData) => {
    return apiClient.post(AUTH_ENDPOINTS.COMPLETE_PROFILE, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  },

  // ========== Password Management ==========

  // Forgot password - Send OTP to email
  forgotPassword: (email) => {
    return apiClient.post(AUTH_ENDPOINTS.FORGOT_PASSWORD, { email });
  },

  // Reset password - Update password with token
  resetPassword: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.RESET_PASSWORD, data);
  },

  // ========== OTP Management ==========

  // Verify OTP (for password reset)
  verifyOtp: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.VERIFY_OTP, data);
  },

  // Resend OTP
  resendOtp: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.RESEND_OTP, data);
  },

  // ========== User Profile ==========

  // Get current user
  getCurrentUser: () => {
    return apiClient.get(AUTH_ENDPOINTS.GET_CURRENT_USER);
  },

  // View profile
  viewProfile: () => {
    return apiClient.get(AUTH_ENDPOINTS.VIEW_PROFILE);
  },

  // Update profile
  updateProfile: (data) => {
    return apiClient.put(AUTH_ENDPOINTS.UPDATE_PROFILE, data);
  },

  changePassword: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.CHANGE_PASSWORD, data);
  },

  // ========== Token Management ==========

  // Refresh token — backend reads the refresh token from req.body.refreshToken
  refreshToken: () => {
    return apiClient.post(AUTH_ENDPOINTS.REFRESH_TOKEN, {
      refreshToken: getRefreshToken(),
    });
  },

  // ========== Registration Form Data ==========

  // Get all roles for registration dropdown
  getRoles: () => {
    return apiClient.get(AUTH_ENDPOINTS.GET_ROLES);
  },

  // Get all experience levels for registration dropdown
  getExperience: () => {
    return apiClient.get(AUTH_ENDPOINTS.GET_EXPERIENCE);
  },

  // Get all states for registration dropdown
  getAllStates: () => {
    return apiClient.get(AUTH_ENDPOINTS.GET_ALL_STATES);
  },

  // Get all cities for registration dropdown (with optional state filter)
  getAllCities: (params) => {
    return apiClient.get(AUTH_ENDPOINTS.GET_ALL_CITIES, { params });
  },

  // Get location choice options for registration dropdown
  getLocationChoice: () => {
    return apiClient.get(AUTH_ENDPOINTS.GET_LOCATION_CHOICE);
  },

  // ========== Social Login ==========

  // Social login (Google, Facebook, Apple)
  socialLogin: (data) => {
    return apiClient.post(AUTH_ENDPOINTS.SOCIAL_LOGIN, data);
  },

  deleteAccount: () => {
    return apiClient.get(AUTH_ENDPOINTS.DELETEACCOUNT);
  },
};

export default authService;
